import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Check, Lock, LogOut, ShieldCheck, Sparkles } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LS_PAY = "sns_pending_pay";

const FEATURES = [
  "Hasta 50 propiedades y embarcaciones",
  "50 usuarios · 30 empleados activos a la vez",
  "Trabajos y mantenimientos sin límite",
  "Checklists, fotos y firma del cliente",
  "Albaranes, informes e integración Holded",
];

export const savePendingPay = (search) => {
  const pay = new URLSearchParams(search).get("pay");
  if (pay === "monthly" || pay === "annual") localStorage.setItem(LS_PAY, pay);
};

export function TrialGate({ children }) {
  const { user } = useAuth();
  const [blocked, setBlocked] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [checked, setChecked] = useState(false);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/subscription/status");
      setBlocked(!!data.blocked);
      setPricing(data.pricing);
    } catch { /* sin estado: no bloquear */ }
    setChecked(true);
  }, []);

  useEffect(() => { if (user) check(); }, [user, check]);
  useEffect(() => {
    const on = () => check();
    window.addEventListener("sns:trial-expired", on);
    return () => window.removeEventListener("sns:trial-expired", on);
  }, [check]);

  if (blocked) return <TrialExpiredWall pricing={pricing} />;
  if (!checked) return null;
  return children;
}

const PlanOption = ({ title, price, sub, badge, accent, onClick, busy, testid }) => (
  <button
    onClick={onClick}
    disabled={busy}
    className={`relative text-left rounded-3xl p-6 border-2 transition-transform hover:-translate-y-1 active:scale-[0.98] disabled:opacity-60 ${
      accent ? "border-[var(--coral)] bg-orange-50" : "border-[#BFD4F5] bg-white"
    }`}
    data-testid={testid}
  >
    {badge && <span className="absolute -top-3 right-5 bg-[var(--coral)] text-white text-xs font-bold px-3 py-1 rounded-full">{badge}</span>}
    <div className={`text-xs font-extrabold tracking-[0.2em] ${accent ? "text-orange-700" : "text-[var(--sky-text)]"}`}>{title}</div>
    <div className="sns-title text-4xl text-[var(--ink)] mt-2">{price}</div>
    <div className="text-sm text-slate-500 mt-1">{sub}</div>
    <div className={`mt-5 w-full rounded-full py-3 text-center font-bold text-white ${accent ? "bg-[var(--coral)]" : "bg-[var(--sky-text)]"}`}>
      {busy ? "Abriendo pago…" : `Pagar ${title.toLowerCase()}`}
    </div>
  </button>
);

export function TrialExpiredWall({ pricing }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const started = useRef(false);
  const isAdmin = user?.role === "admin";
  const monthly = pricing?.monthly_eur ?? 45.99;
  const yearly = pricing?.yearly_eur ?? 441.5;
  const eur = (v) => v.toFixed(2).replace(".", ",");

  const pay = useCallback(async (priceType) => {
    setBusy(priceType); setErr("");
    try {
      const { data } = await api.post("/billing/checkout", { price_type: priceType, origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; return; }
    } catch (e) { setErr(fmtErr(e)); }
    setBusy(null);
  }, []);

  useEffect(() => {
    if (!isAdmin || started.current) return;
    savePendingPay(location.search);
    const pending = localStorage.getItem(LS_PAY);
    if (pending) {
      started.current = true;
      localStorage.removeItem(LS_PAY);
      pay(pending);
    }
  }, [isAdmin, location.search, pay]);

  return (
    <div className="min-h-screen bg-[#EEF3FA] flex items-center justify-center p-5" data-testid="trial-expired-wall">
      <div className="w-full max-w-3xl bg-white rounded-[32px] shadow-xl overflow-hidden sns-fade-up">
        <div className="bg-[#0B1F3F] text-white px-8 py-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-white/10 flex items-center justify-center mb-4"><Lock size={30} /></div>
          <p className="text-[#7DD3FC] text-xs font-bold tracking-[0.3em]">SMILE &amp; SHINE</p>
          <h1 className="sns-title text-3xl sm:text-4xl mt-2 !text-white" data-testid="trial-expired-title">Tu prueba gratuita ha terminado</h1>
          <p className="text-[#BFD4F5] mt-3 text-sm sm:text-base">No se ha borrado nada: trabajos, fotos y empleados siguen guardados.</p>
        </div>

        {isAdmin ? (
          <div className="p-6 sm:p-8">
            <p className="text-center text-[var(--ink)] font-semibold mb-6">Activa Pro para que tu equipo siga trabajando hoy mismo.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <PlanOption title="MENSUAL" price={`${eur(monthly)}€`} sub="+ IVA al mes · sin permanencia" onClick={() => pay("monthly")} busy={busy === "monthly"} testid="wall-pay-monthly" />
              <PlanOption title="ANUAL" price={`${eur(yearly)}€`} sub={`+ IVA al año · ${eur(yearly / 12)}€/mes`} badge="20 % de descuento" accent onClick={() => pay("annual")} busy={busy === "annual"} testid="wall-pay-annual" />
            </div>
            {err && <div className="mt-4 text-sm text-red-600 bg-red-50 rounded-xl p-3" data-testid="wall-error">{err}</div>}
            <ul className="mt-6 grid sm:grid-cols-2 gap-2">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-[var(--ink)]">
                  <span className="w-5 h-5 rounded-full bg-[var(--sky)] text-[var(--sky-text)] flex items-center justify-center"><Check size={12} strokeWidth={3} /></span>{f}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-slate-500 flex items-center gap-2"><ShieldCheck size={14} /> Pago seguro con Stripe. Se suma el 21 % de IVA al pagar. Cancela cuando quieras.</p>
          </div>
        ) : (
          <div className="p-8 text-center" data-testid="wall-employee-msg">
            <Sparkles className="mx-auto text-[var(--coral)] mb-3" />
            <p className="text-[var(--ink)] font-semibold text-lg">El acceso de tu empresa está en pausa</p>
            <p className="text-slate-500 text-sm mt-2">Avisa a tu gerente para que active el plan Pro. En cuanto lo haga podrás seguir trabajando con normalidad.</p>
          </div>
        )}
        <div className="px-8 pb-8 flex justify-center">
          <button onClick={logout} className="text-sm text-slate-500 hover:text-[var(--ink)] flex items-center gap-2" data-testid="wall-logout">
            <LogOut size={14} /> Cerrar sesión ({user?.email})
          </button>
        </div>
      </div>
    </div>
  );
}
